import classNames from "classnames";
import { uniqueId } from "lodash";
import { FC, useContext, useMemo, useRef } from "react";

import { ExportGapFilterType } from "@app/exports/enumerations";
import { SpecialtyFilter } from "@app/exports/exportModels";
import { QESCheckbox, QESRadioButton } from "@app/shared/qes-standards";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { UserContext, Roles } from "@app/user";

interface SelectSpecialtyFilterProps {
  adequacyAnalysisType?: "county" | "zip";
  disabled: boolean;
  onSpecialtyGapFilterChange: (specialtyGapFilter: ExportGapFilterType) => void;
  onSpecialtyIncludeChange: (selected: boolean, isPhysician: boolean) => void;
  onExpandableSpecialtyIncludeChange: (selected: boolean) => void;
  showExpandableSpecialtyOption: boolean;
  specialtyFilter: SpecialtyFilter;
  testIdSuffix: string;
}

const SelectSpecialtyFilter: FC<SelectSpecialtyFilterProps> = (props: SelectSpecialtyFilterProps) => {
  const userContext = useContext(UserContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const specialtyGapFilters = useMemo(() => {
    const filters = [
      { key: ExportGapFilterType.All, value: "All Specialties" },
    ];

    if (props.adequacyAnalysisType === "county") {
      filters.push(
        { key: ExportGapFilterType.Gaps, value: "Specialties with Gaps" },
        { key: ExportGapFilterType.FillableBySingleProvider, value: "Specialties with Gaps Fillable by Single Provider" },
        { key: ExportGapFilterType.Exceptions, value: "Specialties with Exceptions" },
      );

      if (userContext.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
        filters.push(
          { key: ExportGapFilterType.FillableGaps, value: "Specialties with Fillable Gaps" }
        )
      }
      if (featureFlags.has(Features.GAP_FILL_REPORT_UPDATES)) {
        filters.push(
          { key: ExportGapFilterType.GapResolutionStatus, value: "Specialties with Gap Resolution Status" }
        )
      }
    } else if (props.adequacyAnalysisType === "zip") {
      filters.push(
        { key: ExportGapFilterType.NotMeetingAccess, value: "Specialties not meeting access" },
      )
    }

    return filters;
  }, [featureFlags, userContext.roles, props.adequacyAnalysisType]);


  const hasIncludes = props.specialtyFilter.includeFacilitySpecialties || props.specialtyFilter.includePhysicianSpecialties;

  const cbPhysicianId = useRef(uniqueId("include-physician")).current;
  const cbFacilityId = useRef(uniqueId("include-facility")).current;
  const cbExpandableSpecialitesId = useRef(uniqueId("include-expandable-specialties")).current;

  const expandableSpecialtiesCheckbox = props.showExpandableSpecialtyOption &&
    <QESCheckbox
      containerClassName="form-check"
      className="form-check-input mt-0"
      label="Expandable Specialties"
      id={cbExpandableSpecialitesId}
      value="expandable-specialties"
      checked={props.specialtyFilter.includeExpandableSpecialties}
      onChange={(e) => props.onExpandableSpecialtyIncludeChange(e.target.checked)}
      testId="select-specialty-filter__expandable-specialties"
    />

  return (
    <>
      <p className="lead mb0 font-weight-bold">Specialty Filter{props.disabled && <span>: Selected</span>}</p>
      <p>Please select the specialties to include:</p>

      <div className={classNames("form-group row", { "text-danger": !hasIncludes })}>
        <label className="col-12 col-form-label ptb0 text-left">
          Include {!hasIncludes && <i className="fa fa-exclamation-triangle mt5" style={{ position: "absolute" }} />}
        </label>

        <div className="col-12 mt0">
          <QESCheckbox
            containerClassName="form-check"
            className="form-check-input mt-0"
            label="Physician Specialties"
            id={cbPhysicianId}
            disabled={props.disabled}
            value="physician"
            checked={props.specialtyFilter.includePhysicianSpecialties}
            onChange={(e) => props.onSpecialtyIncludeChange(e.target.checked, true)}
            testId="select-specialty-filter__physician"
          />
          <QESCheckbox
            containerClassName="form-check"
            className="form-check-input mt-0"
            label="Facility Specialties"
            id={cbFacilityId}
            disabled={props.disabled}
            value="facility"
            checked={props.specialtyFilter.includeFacilitySpecialties}
            onChange={(e) => props.onSpecialtyIncludeChange(e.target.checked, false)}
            testId="select-specialty-filter__facility"
          />
          {expandableSpecialtiesCheckbox}
        </div>
      </div>

      <div className="form-group row qes-radio-group__container">
        <p className="col-12 col-form-label ptb0 text-left mb-1">Filter</p>
        {specialtyGapFilters.map(({ key, value }) => {
          return (
            <div key={key} className="col-12 mt0">
              <QESRadioButton
                label={value}
                className="qes-radio mt-0"
                disabled={props.disabled}
                value={key}
                checked={props.specialtyFilter.filterType === key}
                onChange={(e) => e.target.checked ? props.onSpecialtyGapFilterChange(key) : {}}
                testId={`select-specialty-filter__${key}--${props.testIdSuffix}`}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

export default SelectSpecialtyFilter;
